/* ---------------------------------------------------------------------------
 * Imagine 360 viewer — dynamic camera thumbnails
 * ---------------------------------------------------------------------------
 * The imagine.io viewer (#modelViewerFrame) posts a message like:
 *   { type: 'VIEW_THUMBNAILS', thumbnails: [{ id, name, url }, ...] }
 * When that arrives we (re)build the thumbnails inside
 * `.product-single__thumbnail--imagine` from the received list. Clicking any
 * thumbnail posts a camera-select message back to the iframe.
 *
 * This block is intentionally self-contained and registered at the top level
 * (NOT inside the DOMContentLoaded handler below, which targets the visualizer
 * page and can throw on products without those elements). That keeps the
 * thumbnail sync working regardless of the rest of this file.
 * ------------------------------------------------------------------------- */
(function () {
  // Guard against double-initialization if this file is enqueued more than once
  // (e.g. a PDP that also renders a quick view of another Imagine product).
  if (window.__imagineViewerThumbsInit) return;
  window.__imagineViewerThumbsInit = true;

  // Each camera view is its own thumbnail cell now (not one clubbed container).
  const CELL_SELECTOR = '.product-single__thumbnail--imagine';
  const THUMBS_SELECTOR = '[data-product-single-media-thumbs]';

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // The 360 view badge: rotation icon stacked above the "360" label. Kept in sync
  // with the static markup in snippets/product.liquid.
  const VIEW_360_BADGE =
    '<span class="view-thumb-badge">' +
    '<svg viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<ellipse cx="12" cy="8" rx="10" ry="4.2" stroke="currentColor" stroke-width="1.3" />' +
    '<path d="M6.4 4.7 L3.7 5.1 L5 7.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" />' +
    '<path d="M17.6 11.3 L20.3 10.9 L19 8.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" />' +
    '</svg>' +
    '<span>360</span>' +
    '</span>';

  // Build one full thumbnail CELL. The 360 view always shows the rotation badge;
  // otherwise shows the render image when a url is present, else a text label.
  // `thumbnailId` is the shared viewer-slide id so the theme's keyboard activation
  // maps to the viewer slide.
  function buildThumbnail(thumb, thumbnailId) {
    const name = escapeHtml(thumb && thumb.name);
    const id = escapeHtml(thumb && thumb.id);
    const url = escapeHtml(thumb && thumb.url);
    const rawName = thumb && thumb.name != null ? String(thumb.name) : '';
    const imgHtml = url
      ? '<img class="product-single__thumbnail-360-img" src="' + url + '" alt="' + name + '" loading="lazy">'
      : '';
    let inner;
    let linkClass = 'product-single__thumbnail-link product-single__thumbnail-link--360';
    if (rawName === '360') {
      // 360 view: rotation badge on top, the render image (if any) below it.
      inner = VIEW_360_BADGE + imgHtml;
      if (imgHtml) linkClass += ' product-single__thumbnail-link--360-media';
    } else if (imgHtml) {
      inner = imgHtml;
    } else {
      inner = '<span class="product-single__thumbnail-360">' + name + '</span>';
    }

    return (
      '<div class="product-single__thumbnail product-single__thumbnail--imagine"' +
      ' data-id="imagine"' +
      ' data-thumbnail-id="' + escapeHtml(thumbnailId) + '"' +
      ' data-imagine-camera="' + name + '"' +
      ' data-imagine-id="' + id + '"' +
      ' aria-label="' + name + ' view">' +
      '<span class="' + linkClass + '">' +
      inner +
      '</span>' +
      '</div>'
    );
  }

  // Rebuild the camera cells from a VIEW_THUMBNAILS payload. The cells are
  // separate Flickity nav cells, so we swap the DOM then ask Flickity to reload.
  function renderThumbnails(thumbnails) {
    if (!Array.isArray(thumbnails)) return;

    const thumbs = document.querySelector(THUMBS_SELECTOR);
    if (!thumbs) return;

    const existing = Array.from(thumbs.querySelectorAll(CELL_SELECTOR));
    if (!existing.length) return; // not an imagine gallery

    const thumbnailId = existing[0].getAttribute('data-thumbnail-id') || '';
    const anchor = existing[0];

    // Remember the selected camera so the rebuild doesn't reset it to the first cell.
    const selected = existing.find((el) => el.classList.contains('is-nav-selected'));
    const selectedId = selected ? selected.getAttribute('data-imagine-id') : null;
    const selectedName = selected ? selected.getAttribute('data-imagine-camera') : null;

    // Insert the new cells in front of the first old one, then drop the old set.
    anchor.insertAdjacentHTML(
      'beforebegin',
      thumbnails.map((t) => buildThumbnail(t, thumbnailId)).join('')
    );
    existing.forEach((el) => el.remove());

    // Restore the selection on the matching new cell (by id, falling back to name).
    if (selected) {
      const cells = Array.from(thumbs.querySelectorAll(CELL_SELECTOR));
      const match =
        (selectedId && cells.find((el) => el.getAttribute('data-imagine-id') === selectedId)) ||
        (selectedName && cells.find((el) => el.getAttribute('data-imagine-camera') === selectedName));
      if (match) match.classList.add('is-nav-selected');
    }

    // The thumbnails are Flickity nav cells; reload + re-measure after the swap.
    try {
      if (window.theme && window.theme.Flickity) {
        const flkty = window.theme.Flickity.data(thumbs);
        if (flkty) {
          flkty.reloadCells();
          flkty.resize();
        }
      }
    } catch (e) {
      /* no-op: reload is best-effort */
    }

    // Re-apply the thumbnail->slide highlight remap after the cells change.
    if (typeof window.__imagineSyncNav === 'function') window.__imagineSyncNav();
  }

  function handleViewerMessage(event) {
    // console.log("handleViewerMessage",event.data);
    const data = event && event.data;
    if (!data || typeof data !== 'object' || data.type !== 'VIEW_THUMBNAILS') return;
    renderThumbnails(data.thumbnails);
  }

  // Event delegation so dynamically-rebuilt thumbnails keep working without rebinding.
  function handleThumbnailClick(event) {
  
    const label = event.target.closest && event.target.closest('[data-imagine-camera]');
    if (!label) return;

    const iframe = document.getElementById('modelViewerFrame');
    if (!iframe || !iframe.contentWindow) return;

    // Edit this payload to match what the imagine.io viewer expects for selecting a view.
    iframe.contentWindow.postMessage(
      {
        type: 'camera',
        value: label.getAttribute('data-imagine-camera'),
        id: label.getAttribute('data-imagine-id'),
      },
      '*'
    );
  }

  function initImagineThumbnailClicks() {
    // Delegated on document: the camera cells are rebuilt on VIEW_THUMBNAILS, so
    // a single document-level listener keeps working without rebinding.
    document.addEventListener('click', handleThumbnailClick);
  }

  // Generic viewer message buttons: any element with data-imagine-message-type
  // posts { type, value } to the iframe on click. Example:
  //   <button data-imagine-message-type="Upholstery" data-imagine-message-value="21">
  // -> postMessage({ type: 'Upholstery', value: '21' }, '*')
  function handleMessageButtonClick(event) {
    const trigger =
      event.target.closest && event.target.closest('[data-imagine-message-type]');
    if (!trigger) return;

    const iframe = document.getElementById('modelViewerFrame');
    if (!iframe || !iframe.contentWindow) return;

    console.log('Sending message to viewer:', {
      type: trigger.getAttribute('data-imagine-message-type'),
      value: trigger.getAttribute('data-imagine-message-value'),
    });

    iframe.contentWindow.postMessage(
      {
        type: trigger.getAttribute('data-imagine-message-type'),
        value: trigger.getAttribute('data-imagine-message-value'),
      },
      '*'
    );
  }

  // Delegated on document so it covers buttons placed anywhere on the page.
  document.addEventListener('click', handleMessageButtonClick);

  window.addEventListener('message', handleViewerMessage);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initImagineThumbnailClicks);
  } else {
    initImagineThumbnailClicks();
  }
})();

/* ---------------------------------------------------------------------------
 * 360 frame image -> cart line-item property
 * ---------------------------------------------------------------------------
 * The viewer posts { type: '360_FRAME_IMAGE', frame, url } whenever a finish
 * changes. We store the latest url on window.__customFrameImageUrl and mirror
 * it into a hidden product-form input named properties[customimageurl] so it is
 * submitted with Add to Cart and rendered in the cart line item.
 *
 * Registered at the top level (NOT inside the DOMContentLoaded handler below)
 * so it ALWAYS receives the message, regardless of when this script loads or
 * whether the product-page init throws on products without certain elements.
 * ------------------------------------------------------------------------- */
(function () {
  if (window.__imagineFrameImageInit) return;
  window.__imagineFrameImageInit = true;

  // Write/update the hidden line-item property on the product form. Safe to call
  // before the form exists — it just no-ops until the PDP form is in the DOM.
  window.__writeCustomImageUrlInput = function (url) {
    // The real <form> is the one the Shopify {% form 'product' %} tag renders —
    // it carries [data-product-form], NOT the class "product__form" (that class
    // is on a parent <div>). Match it robustly.
    const productForm =
      document.querySelector('product-form form[data-product-form]') ||
      document.querySelector('form[data-product-form]') ||
      document.querySelector('.product__form form');
    if (!productForm) {
      console.warn('[customimageurl] product form not found yet — will retry on next frame');
      return;
    }
    if (!url) return;

    let input = document.querySelector('#customimageurl-property');
    if (input) {
      input.value = url;
    } else {
      productForm.insertAdjacentHTML(
        'afterbegin',
        '<input id="customimageurl-property" type="hidden" name="properties[_customimageurl]" value="' + url + '" />'
      );
    }
    console.log('[_customimageurl] hidden input set to:', url);
  };

  window.addEventListener('message', function (event) {
    const data = event.data;
    if (!data || data.type !== '360_FRAME_IMAGE') return;

    console.log('Received frame', data.frame, 'url:', data.url);
    window.__customFrameImageUrl = data.url || null;
    window.__writeCustomImageUrlInput(window.__customFrameImageUrl);
  });
})();

/* ---------------------------------------------------------------------------
 * Variant option selectors -> viewer postMessage
 * ---------------------------------------------------------------------------
 * The theme's standard variant pickers (radio + swatch) in
 * snippets/product-option-value.liquid carry [data-single-option-selector].
 * When the shopper changes one, mirror the selection to the Imagine viewer so
 * the 3D model updates. Posts:
 *   { type: <option name>, value: <selected value>, id: <option value id> }
 *
 * Registered at the top level and delegated on document so it keeps working
 * across the variant-driven DOM re-renders the theme performs.
 * ------------------------------------------------------------------------- */
(function () {
  if (window.__imagineOptionSelectorInit) return;
  window.__imagineOptionSelectorInit = true;

  // Config ids whose viewer needs "Wood Top" to mirror "Paint Color" whenever
  // "Top Finish" is set to "Painted" (kept in sync with the same list in the
  // DOMContentLoaded handler below).
  const woodTopConfigIds = [
    '144', '145',
    '95', '96', '97', '105',
    '106', '107', '110', '168', '171', '173',
    '177', '178', '194', '143', '141', '140', '147',
    '146', '172', '148', '169',
    '336', '348', '343', '344', '346', '345', '347', '336', '337',
  ];

  const shelfiePaintedConfigSpecialCaseIds = [
    '401'
  ]

  // Pull the option name out of name="options[Paint Color]".
  function getOptionName(input) {
    const match = /options\[(.+)\]/.exec(input.getAttribute('name') || '');
    return match ? match[1] : '';
  }

  // Current value of a named product option, from whichever selector renders
  // it (plain radio or swatch) -- both carry [data-single-option-selector].
  function getOptionValue(name) {
    const checked = document.querySelector(
      '[data-single-option-selector][name="options[' + name + ']"]:checked'
    );
    return checked ? checked.value : null;
  }

  document.addEventListener('change', function (event) {
    const input =
      event.target.closest && event.target.closest('[data-single-option-selector]');
    if (!input) return;

    const iframe = document.getElementById('modelViewerFrame');
    if (!iframe || !iframe.contentWindow) return;

    const optionName = getOptionName(input);
    const imagineUUid = document.querySelector('#imagineUuid')?.value;

    // Wood-top products: "Paint Color" and "Top Finish" are linked -- changing
    // either one must re-post BOTH, with "Wood Top" mirroring "Paint Color"
    // whenever "Top Finish" is "Painted" (otherwise "Wood Top" is the Top
    // Finish selection itself). Some configurators name this option "Wood Top"
    // directly instead of "Top Finish" -- treat the two names as the same option.
    if (woodTopConfigIds.includes(imagineUUid) && (optionName === 'Paint Color' || optionName === 'Top Finish' || optionName === 'Wood Top')) {
      const paintColor = optionName === 'Paint Color' ? input.value : getOptionValue('Paint Color');
      const topFinish = (optionName === 'Top Finish' || optionName === 'Wood Top')
        ? input.value
        : (getOptionValue('Top Finish') ?? getOptionValue('Wood Top'));
      const woodTop = (topFinish || '').trim().toUpperCase() === 'PAINTED' ? paintColor : topFinish;

      const multiLabelData = {
        type: 'customMultiLabel',
        'Paint Color': paintColor,
        'Wood Top': woodTop
      };
      console.log('Option selector -> viewer:', multiLabelData);
      iframe.contentWindow.postMessage(multiLabelData, '*');
      return;
    }
    else if(shelfiePaintedConfigSpecialCaseIds.includes(imagineUUid)){
    // console.log(el.dataset.group === 'Paint Color' ? 'Wood Top' : 'Paint Color');
      const updatedShelfieLabel = optionName === 'Paint Color' ? 'Wood Top' : 'Paint Color'
      const data_new = {
        type: updatedShelfieLabel,
        value: input.value
      };
      iframe.contentWindow.postMessage(data_new, '*');
      return;
    }

    const data = {
      type: optionName,
      value: input.value
    };

    console.log('Option selector -> viewer:', data);
    iframe.contentWindow.postMessage(data, '*');
  });
})();

/* ---------------------------------------------------------------------------
 * Initial selection state -> viewer postMessage
 * ---------------------------------------------------------------------------
 * On page load, snapshot whatever variant options are already selected --
 * Paint Color, Wood Top, Size, or whatever other sections this particular
 * product actually renders -- and mirror that starting selection to the
 * viewer as soon as it's ready.
 *
 * woodTopConfigIds products: posted as ONE combined message --
 *   { type: 'customMultiLabel', <option name>: <selected value>, ... }
 * -- with "Top Finish" reported as "Wood Top" (same rename used by the
 * change listener above), mirroring "Paint Color" whenever Top Finish is
 * "Painted" (the viewer's painted wood-top layer is driven by the paint
 * color, not the literal "Painted" label).
 *
 * Every other config: posted as a SEPARATE message per section, exactly as
 * presented in the UI --
 *   { type: <option name>, value: <selected value> }  (one postMessage each)
 *
 * Registered at the top level, self-contained, and fires when the iframe
 * tells us it's ready via { type: 'CONFIGURATOR_LOADED' } -- posting on
 * window 'load' raced the iframe's own startup, since the iframe can still
 * be initializing after the parent page has finished loading.
 * ------------------------------------------------------------------------- */
(function () {
  if (window.__imagineInitialSelectionInit) return;
  window.__imagineInitialSelectionInit = true;

  // Kept in sync with the same list used by the change listener above -- only
  // these configs get the "Top Finish" -> "Wood Top" rename + Painted-color
  // substitution; every other config is reported as-is.
  const woodTopConfigIds = [
    '144', '145',
    '95', '96', '97', '105',
    '106', '107', '110', '168', '171', '173',
    '177', '178', '194', '143', '141', '140', '147',
    '146', '172', '148', '169',
    '336', '348', '343', '344', '346', '345', '347', '336', '337',
  ];

  const shelfiePaintedConfigSpecialCaseIds = [
    '401'
  ]

  // Pull the option name out of name="options[Paint Color]".
  function getOptionName(input) {
    const match = /options\[(.+)\]/.exec(input.getAttribute('name') || '');
    return match ? match[1] : '';
  }

  function postInitialSelectionState() {
    const iframe = document.getElementById('modelViewerFrame');
    if (!iframe || !iframe.contentWindow) return;

    const imagineUUid = document.querySelector('#imagineUuid')?.value;
    const isWoodTopConfig = woodTopConfigIds.includes(imagineUUid);
    const isShelfiePaintedConfigSpecialCase = shelfiePaintedConfigSpecialCaseIds.includes(imagineUUid);

    const result = {};
    document.querySelectorAll('[data-single-option-selector]:checked').forEach((input) => {
      const name = getOptionName(input);
      if (!name) return;
      const label = isWoodTopConfig && name === 'Top Finish' ? 'Wood Top' : name;
      result[label] = input.value;
    });

    if (!Object.keys(result).length) return;

    if (isWoodTopConfig) {
      if (result['Wood Top'] && result['Wood Top'].trim().toUpperCase() === 'PAINTED' && result['Paint Color']) {
        result['Wood Top'] = result['Paint Color'];
      }

      const data = { type: 'customMultiLabel', ...result };
      console.log('Initial selection -> viewer:', data);
      iframe.contentWindow.postMessage(data, '*');
      return;
    }
    else if(isShelfiePaintedConfigSpecialCase){
      const dataArray = Object.keys(result).map((label) => {
        const updatedShelfieLabel = label === 'Paint Color' ? 'Wood Top' : 'Paint Color';
        return { type: updatedShelfieLabel, value: result[label] };
      });
      const payload = dataArray.length === 1 ? dataArray[0] : dataArray;
      console.log('Initial selection -> viewer:', payload);
      iframe.contentWindow.postMessage(payload, '*');
      return;
    }

    // Every other config: one { type, value } pair per section. Sent as a
    // single object when there's only one, otherwise as an array.
    const dataArray = Object.keys(result).map((label) => ({ type: label, value: result[label] }));
    const payload = dataArray.length === 1 ? dataArray[0] : dataArray;
    console.log('Initial selection -> viewer:', payload);
    iframe.contentWindow.postMessage(payload, '*');
  }

  window.addEventListener('message', function (event) {
    const data = event && event.data;
    if (!data || typeof data !== 'object' || data.type !== 'CONFIGURATOR_LOADED') return;
    console.log("message______received___at____parent",data);
    postInitialSelectionState();
  });
})();

/* ---------------------------------------------------------------------------
 * Imagine 360 viewer — unified grouped gallery navigation
 * ---------------------------------------------------------------------------
 * Fixes two things for galleries that contain the Imagine viewer:
 *
 *  (1) THUMBNAIL CLICKS. The thumbnail strip is a Flickity `asNavFor` nav that
 *      maps each nav cell to a slide BY INDEX. Each camera view is its own cell
 *      but there is only ONE viewer slide, so the extra cells offset every image
 *      thumbnail from its slide (clicking a thumb selects the wrong slide, or
 *      nothing). We disable Flickity's index-based click + highlight and drive
 *      selection ourselves by ELEMENT, so every thumbnail selects its real slide.
 *
 *  (2) GROUPED BACK / NEXT. Thumbnails belong to two groups:
 *        Group A ("imagine") = the camera cells + any image whose alt contains
 *                              "imagine".
 *        Group B ("normal")  = every other image / media.
 *      Arrow behavior (over the ordered thumbnail list):
 *        Next: last of A -> first of B; last of B -> first of A; else next in group.
 *        Back: first of A -> last of B; first of B -> last of A; else prev in group.
 *      Because the camera cells are the leading Group-A items, stepping Next from
 *      360 -> Cam1 -> (imagine images) preserves the camera-view cycling.
 *
 * One handler owns both behaviors because Group A spans camera cells AND imagine
 * images, so they cannot be cleanly separated into two competing handlers.
 * ------------------------------------------------------------------------- */
(function () {
  if (window.__imagineGalleryNavInit) return;
  window.__imagineGalleryNavInit = true;

  // Escape for use inside a QUOTED attribute selector value, e.g. [data-id="..."].
  // Do NOT use CSS.escape here: it escapes leading digits for identifier context
  // (CSS.escape('12345') -> '\\31 2345'), which would not match a numeric media id
  // inside quotes. Quoted values only need quotes/backslashes escaped.
  function attrEscape(value) {
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function getFlkty(el) {
    try {
      return el && window.theme && window.theme.Flickity ? window.theme.Flickity.data(el) : null;
    } catch (e) {
      return null;
    }
  }

  function isImagineGallery(gallery) {
    return !!gallery.querySelector('.product-single__thumbnail--imagine');
  }
  function getStrip(gallery) {
    return gallery.querySelector('[data-product-single-media-thumbs]');
  }
  function getMain(gallery) {
    return gallery.querySelector('[data-product-single-media-slider]');
  }

  // Ordered navigable thumbnail cells (camera cells + image thumbs), DOM order.
  function getThumbs(strip) {
    return strip ? Array.from(strip.querySelectorAll('.product-single__thumbnail')) : [];
  }
  function isCameraCell(cell) {
    return cell.classList.contains('product-single__thumbnail--imagine');
  }

  // Group of a thumbnail: 'A' (imagine) for camera cells or images whose alt
  // contains "imagine"; otherwise 'B' (normal).
  function groupOf(cell) {
    if (isCameraCell(cell)) return 'A';
    const img = cell.querySelector('img');
    const alt = ((img && img.getAttribute('alt')) || '').toLowerCase();
    return alt.indexOf('imagine') > -1 ? 'A' : 'B';
  }

  // The slide a thumbnail represents: camera cells -> the viewer slide; image
  // cells -> the slide with the same data-id. NOTE: we match on data-id (= media.id),
  // NOT data-thumbnail-id/data-media-id — in this theme media-thumb is rendered
  // without `unique`, so the thumb's id (section.id-mediaId) does not match the
  // slide's data-media-id (section.id-productId-mediaId). data-id is consistent.
  function slideFor(main, cell) {
    if (isCameraCell(cell)) return main.querySelector('[data-type="imagine"], [data-id="imagine"]');
    const id = cell.getAttribute('data-id');
    return id ? main.querySelector('.product-gallery__media-slide[data-id="' + attrEscape(id) + '"]') : null;
  }

  function clearHighlights(strip) {
    strip.querySelectorAll('.is-nav-selected').forEach((c) => c.classList.remove('is-nav-selected'));
  }
  function highlight(strip, cell) {
    clearHighlights(strip);
    if (cell) cell.classList.add('is-nav-selected');
  }

  function postCamera(cell) {
    const iframe = document.getElementById('modelViewerFrame');
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage(
      {
        type: 'camera',
        value: cell.getAttribute('data-imagine-camera'),
        id: cell.getAttribute('data-imagine-id'),
      },
      '*'
    );
  }

  // Activate a thumbnail: select its slide. Camera cells also set the camera
  // highlight and (when navigating via arrows) post the camera message. Camera
  // clicks are posted by the dedicated handler in the first IIFE, so we pass
  // doPostCamera = false from the click path to avoid double-posting.
  function activate(gallery, cell, doPostCamera) {
    const main = getMain(gallery);
    const strip = getStrip(gallery);
    const mainFlkty = getFlkty(main);
    if (!main || !strip) return;

    const slide = slideFor(main, cell);
    if (isCameraCell(cell)) {
      highlight(strip, cell); // set camera highlight first so syncHighlight keeps it
      if (slide && mainFlkty) mainFlkty.selectCell(slide, false, true);
      if (doPostCamera) postCamera(cell);
    } else if (slide) {
      if (mainFlkty) mainFlkty.selectCell(slide, false, true); // highlight via main 'select'
      else highlight(strip, cell);
    }
  }

  // The currently active thumbnail: on the viewer slide it is the active camera
  // cell; otherwise the image thumb matching the selected slide.
  function currentThumb(gallery) {
    const main = getMain(gallery);
    const strip = getStrip(gallery);
    const mainFlkty = getFlkty(main);
    const thumbs = getThumbs(strip);
    if (!thumbs.length) return null;

    const slide =
      (mainFlkty && mainFlkty.selectedElement) ||
      main.querySelector('.product-gallery__media-slide.is-selected');
    const onImagine =
      slide && (slide.getAttribute('data-type') === 'imagine' || slide.getAttribute('data-id') === 'imagine');

    if (onImagine) {
      const cams = thumbs.filter(isCameraCell);
      return cams.find((c) => c.classList.contains('is-nav-selected')) || cams[0] || null;
    }
    if (slide) {
      const sid = slide.getAttribute('data-id');
      const cell = thumbs.find((c) => !isCameraCell(c) && c.getAttribute('data-id') === sid);
      if (cell) return cell;
    }
    return thumbs[0];
  }

  // Resolve the target thumbnail for a Back/Next press using the group rules.
  function targetThumb(gallery, dir) {
    const thumbs = getThumbs(getStrip(gallery));
    if (thumbs.length <= 1) return thumbs[0] || null;

    const groups = { A: [], B: [] };
    thumbs.forEach((t) => groups[groupOf(t)].push(t));

    const cur = currentThumb(gallery);
    if (!cur) return thumbs[0];

    const g = groupOf(cur);
    const list = groups[g];
    const other = groups[g === 'A' ? 'B' : 'A'];
    const k = list.indexOf(cur);

    if (dir === 'next') {
      if (k === list.length - 1) return other.length ? other[0] : list[0]; // last of group -> first of other (wrap if empty)
      return list[k + 1]; // next within group
    }
    // previous
    if (k === 0) return other.length ? other[other.length - 1] : list[list.length - 1]; // first of group -> last of other
    return list[k - 1]; // previous within group
  }

  function navigate(gallery, dir) {
    const target = targetThumb(gallery, dir);
    if (target) activate(gallery, target, true);
  }

  // Mirror the selected slide onto the thumbnails (replaces asNavFor's index
  // highlight). Camera highlight while on the viewer slide is owned by activate().
  function syncHighlight(gallery) {
    const main = getMain(gallery);
    const strip = getStrip(gallery);
    const mainFlkty = getFlkty(main);
    if (!main || !strip) return;

    const slide =
      (mainFlkty && mainFlkty.selectedElement) ||
      main.querySelector('.product-gallery__media-slide.is-selected');
    if (!slide) return;

    const onImagine =
      slide.getAttribute('data-type') === 'imagine' || slide.getAttribute('data-id') === 'imagine';

    if (onImagine) {
      strip
        .querySelectorAll('.product-single__thumbnail:not(.product-single__thumbnail--imagine)')
        .forEach((c) => c.classList.remove('is-nav-selected'));
      const cams = getThumbs(strip).filter(isCameraCell);
      if (cams.length && !cams.some((c) => c.classList.contains('is-nav-selected'))) {
        cams[0].classList.add('is-nav-selected');
      }
    } else {
      clearHighlights(strip);
      const sid = slide.getAttribute('data-id');
      const cell = getThumbs(strip).find(
        (c) => !isCameraCell(c) && c.getAttribute('data-id') === sid
      );
      if (cell) cell.classList.add('is-nav-selected');
    }
  }
  window.__imagineSyncNav = function () {
    document.querySelectorAll('.product-gallery').forEach((g) => {
      if (isImagineGallery(g)) syncHighlight(g);
    });
  };

  // ---- Back / Next arrows: fully control imagine galleries (capture phase) ----
  document.addEventListener(
    'click',
    function (event) {
      const btn = event.target.closest && event.target.closest('.flickity-prev-next-button');
      if (!btn) return;
      const gallery = btn.closest('.product-gallery');
      if (!gallery || !isImagineGallery(gallery)) return;

      // We define behavior for every case, so block Flickity's own arrow nav.
      event.preventDefault();
      event.stopImmediatePropagation();
      navigate(gallery, btn.classList.contains('next') ? 'next' : 'previous');
    },
    true
  );

  // ---- Thumbnail clicks: select the correct slide (fixes Issue 1) ----
  document.addEventListener('click', function (event) {
    const cell = event.target.closest && event.target.closest('.product-single__thumbnail');
    if (!cell) return;
    const gallery = cell.closest('.product-gallery');
    if (!gallery || !isImagineGallery(gallery)) return;
    activate(gallery, cell, false); // camera message posted by the first IIFE's handler
  });

  // ---- Patch Flickity: drop asNavFor's index click + highlight, wire ours ----
  function patch(gallery) {
    if (!isImagineGallery(gallery)) return true; // leave normal galleries on native asNavFor
    const main = getMain(gallery);
    const strip = getStrip(gallery);
    if (!main || !strip) return true; // single-media: no nav

    const mainFlkty = getFlkty(main);
    const navFlkty = getFlkty(strip);
    if (!mainFlkty || !navFlkty) return false; // Flickity not ready — retry
    // asNavFor wires in a setTimeout AFTER the nav is created; wait for it so our
    // off()/on() overrides are not clobbered when it wires up afterwards.
    if (!navFlkty.navCompanion) return false;
    if (navFlkty.__imaginePatched) return true;
    navFlkty.__imaginePatched = true;

    if (navFlkty.onNavStaticClick) navFlkty.off('staticClick', navFlkty.onNavStaticClick); // disable index click nav
    if (navFlkty.onNavCompanionSelect) mainFlkty.off('select', navFlkty.onNavCompanionSelect); // disable index highlight
    mainFlkty.on('select', function () {
      syncHighlight(gallery);
    });

    syncHighlight(gallery);
    // Default the camera highlight to the first camera so a view is always shown.
    const cams = getThumbs(strip).filter(isCameraCell);
    if (cams.length && !cams.some((c) => c.classList.contains('is-nav-selected'))) {
      cams[0].classList.add('is-nav-selected');
    }
    return true;
  }

  let scheduled = false;
  let tries = 0;
  function run() {
    scheduled = false;
    let done = true;
    document.querySelectorAll('.product-gallery').forEach((g) => {
      if (!patch(g)) done = false;
    });
    if (!done && tries++ < 180) requestAnimationFrame(run);
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    tries = 0;
    requestAnimationFrame(run);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  // Theme editor + variant re-renders replace product-info (new Flickity); re-patch.
  document.addEventListener('shopify:section:load', schedule);
  try {
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  } catch (e) {
    /* no-op: MutationObserver unavailable */
  }
})();

document.addEventListener('DOMContentLoaded', function () {
  const visualizerOptionsContainer = document.querySelector('.product__selectors--visualizer');
  const optionsContainer = document.querySelector('.product__selectors:not(.product__selectors--visualizer)');
  const addToCartButton = document.querySelector('.btn--submit');
  // The submit button lives inside the Shopify {% form 'product' %} <form>, so
  // its .closest('form') is the exact form that gets serialized on add-to-cart.
  // (The "product__form" class is on a parent <div>, not the <form>.)
  const productForm =
    (addToCartButton && addToCartButton.closest('form')) ||
    document.querySelector('product-form form[data-product-form]') ||
    document.querySelector('form[data-product-form]');
  const filters = document.querySelectorAll('.custom-option__filters');
  const imagineUUid = document.querySelector('#imagineUuid')?.value;
  const woodTopConfigIds = [
    '144','145',
    '95','96','97','105',
    '106','107','110','168','171','173',
    '177','178','194','143','141','140','147',
    '146','172','148','169',
    '336','348','343','344','346','345','347','336','337',
  ];

  const shelfiePaintedConfigIds = [
    '387','389','391','393','395','397','399'
  ]
  const shelfiePaintedConfigSpecialCaseIds = [
    '401'
  ]
  const noCushionConfigIds = ['130','121','113','123','128',
    '129','174','115','117','136','424','426'
  ];

  optionsContainer.addEventListener('change', function (e) {
    if (!e.target.matches('.radio__input')) return;

    const el = e.target;

    if (el.classList.contains('radio__input')) {
      if (el.name !== "options[Grade]") {
        // console.log("el:",el)
        console.log("values:",el.dataset.value, "type:", el.dataset.group)
        if(woodTopConfigIds.includes(imagineUUid)){
          // console.log("custommmm")
            const visualizerData = getSelectedVisualizerData(el.dataset.value, el.dataset.group);

            console.log(visualizerData,"visualizerData");
        }else if(shelfiePaintedConfigIds.includes(imagineUUid)){
          // console.log(el.dataset.group === 'Paint Color' ? 'Wood Top' : 'Paint Color');
          const updatedShelfieLabel = el.dataset.group === 'Paint Color' ? 'Wood Top' : 'Paint Color'
          // console.log('callexczxccczd',el.dataset.group);
          updateViewer(el.dataset.id, updatedShelfieLabel);
        }else{
          if (!noCushionConfigIds.includes(imagineUUid) || el.dataset.group === 'Chair Frame') {
              updateViewer(el.dataset.value, el.dataset.group);
            }else{
              console.log(el.dataset.id, el.dataset.group);
            }
        }
      }
    }

    // console.log("new values:", el.dataset.value, "type:", el.dataset.group);
  },false);

  function getSelectedVisualizerData(value, type) {
    // console.log(type,"type type");

      const iframe = document.querySelector("#modelViewerFrame");
    if(type === 'Variation'){
      if(iframe) {

        const data = {
          type: type?.trim(),
          value: value?.trim()
        };
        // console.log(data, "dataaaa");
        iframe.contentWindow.postMessage(data, "*");
      }

    }else{

      const result = {};

      document.querySelectorAll('.selector-wrapper').forEach(wrapper => {
        const label = wrapper.getAttribute('data-visualizer-label') === 'Top Finish' ? 'Wood Top' : wrapper.getAttribute('data-visualizer-label');

        const checkedRadio = wrapper.querySelector('input[type="radio"]:checked');

        if (label && checkedRadio && label !== 'Size' && label !== 'Side') {
          const value =
            checkedRadio.getAttribute('data-value') || checkedRadio.value;
          result[label] = value;
        }
      });

      // "Wood Top" mirrors "Paint Color" whenever "Top Finish" is set to
      // "Painted" -- the viewer's painted wood-top layer is driven by the paint
      // color, not the literal "Painted" label. Otherwise "Wood Top" keeps the
      // Top Finish selection as-is (e.g. "Natural Mapel").
      if (result['Wood Top'] && result['Wood Top'].trim().toUpperCase() === 'PAINTED' && result['Paint Color']) {
        result['Wood Top'] = result['Paint Color'];
      }

      if(iframe) {
        // Post one flat { type, value } message per label instead of a single
        // bundled object, so the viewer gets a separate Paint Color and a
        // separate Wood Top message.
        Object.keys(result).forEach(label => {
          const data = { type: label, value: result[label] };
          console.log(data, "dataaaa");
          iframe.contentWindow.postMessage(data, "*");
        });
      }

      return result;
    }
  }

  if (filters) {
    filters.forEach(filter => {

      const filtersObject = {};
      const filterGroups = filter.querySelectorAll('div');
      filterGroups.forEach(filterGroup => {
        const group = filterGroup.dataset.group;
        if (!filtersObject.hasOwnProperty(group)) {
          filtersObject[group] = "";
        }
      });

      filter.addEventListener('change', e => {
        const el = e.target;
        const parentContainer = el.closest('.custom-option');
        const filterVal = el.value;
        const group = el.dataset.group;

        if (filterVal === "all") {
          filtersObject[group] = "";
          const filterString = Object.keys(filtersObject).reduce((previousValue, currentValue) => {
            return previousValue + filtersObject[currentValue];
          }, '');

          if (filterString === "") {
            parentContainer.querySelectorAll(`.radio__button--custom`).forEach(element => {
              element.style.display = 'block';
            });
          } else {
            parentContainer.querySelectorAll(`.radio__button--custom[data-filter*="${filterString}"]`).forEach(element => {
              element.style.display = 'block';
            });

            parentContainer.querySelectorAll(`.radio__button--custom:not([data-filter*="${filterString}"])`).forEach(element => {
              element.style.display = 'none';
            });
          }
        } else {
          filtersObject[group] = filterVal;
          const filterString = Object.keys(filtersObject).reduce((previousValue, currentValue) => {
            return previousValue + filtersObject[currentValue];
          }, '');

          parentContainer.querySelectorAll(`.radio__button--custom[data-filter*="${filterString}"]`).forEach(element => {
            element.style.display = 'block';
          });

          parentContainer.querySelectorAll(`.radio__button--custom:not([data-filter*="${filterString}"])`).forEach(element => {
            element.style.display = 'none';
          });
        }
      });
    })
  }

  function onMessageHandler(event) {

    if (typeof event.data === "string" && event.data !== '' && event.data.includes('data:image/')) {

      const thumbnailInput = document.querySelector('#thumbnail-property');

      if (thumbnailInput) {
        thumbnailInput.value = event.data;
      } else {
        productForm.insertAdjacentHTML("afterbegin", `<input id="thumbnail-property" type="hidden" name="properties[_thumbnail]" value="${event.data}" />`);
      }

      // Make sure the latest frame image is written before this path submits.
      syncCustomImageUrlProperty();

      addToCartButton.removeEventListener("click", addToCartClickEvent, false);
      addToCartButton.click();
      addToCartButton.addEventListener('click', addToCartClickEvent, false);
    }
  }

  window.addEventListener("message", onMessageHandler);

  // The 360_FRAME_IMAGE message is received at the top level (see the block just
  // above this DOMContentLoaded handler), which keeps window.__customFrameImageUrl
  // up to date and writes the hidden properties[customimageurl] input. This thin
  // wrapper lets the Add to Cart flow (re)write that input right before submit.
  function syncCustomImageUrlProperty() {
    window.__writeCustomImageUrlInput(window.__customFrameImageUrl);
  }

  // Ask the viewer for the current frame image and resolve with its URL.
  // Resolves as soon as a 360_FRAME_IMAGE message arrives; if none arrives
  // within `timeoutMs`, falls back to the last frame received during
  // configuration (or null if none).
  function requestFrameImage(timeoutMs = 2500) {
    return new Promise((resolve) => {
      let settled = false;

      function onMsg(event) {
        const data = event.data;
        if (!data || data.type !== '360_FRAME_IMAGE') return;
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        window.removeEventListener('message', onMsg);
        resolve(data.url || window.__customFrameImageUrl || null);
      }

      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMsg);
        console.warn('[_customimageurl] frame request timed out — using last known frame');
        resolve(window.__customFrameImageUrl || null);
      }, timeoutMs);

      window.addEventListener('message', onMsg);

      // Ping the viewer for the current frame.
      updateViewer('getImage', 'button');
    });
  }

  function addToCartClickEvent(e) {
    if (!!(visualizerAPI)) {
      e.preventDefault();
      e.stopPropagation();

      // Request the current frame image and the thumbnail screenshot in
      // parallel, then write both line-item properties before submitting.
      const framePromise = requestFrameImage();
      const scenePhoto = visualizerAPI.scene.requestScreenshot({ format: 'jpeg', keepPosition: false, horizontalAngle: -35, verticalAngle: 8, width: 320, height: 320 });

      Promise.all([framePromise, scenePhoto]).then(([frameUrl, value]) => {
        if (frameUrl) {
          window.__customFrameImageUrl = frameUrl;
          syncCustomImageUrlProperty();
        }

        const thumbnailInput = document.querySelector('#thumbnail-property');

        if (thumbnailInput) {
          thumbnailInput.value = value.dataUrl;
        } else {
          productForm.insertAdjacentHTML("afterbegin", `<input id="thumbnail-property" type="hidden" name="properties[_thumbnail]" value="${value.dataUrl}" />`);
        }

        addToCartButton.removeEventListener("click", addToCartClickEvent, false);
        addToCartButton.click();
        addToCartButton.addEventListener('click', addToCartClickEvent, false);
      });
    }
  }

  addToCartButton.addEventListener('click', addToCartClickEvent, false);

  function updateViewer(value, type) {
    const iframe = document.querySelector("#modelViewerFrame");

      if(iframe) {

        const data = {
          type: type?.trim(),
          value: value?.trim()
        };
        iframe.contentWindow.postMessage(data, "*");
        console.log(data,'updateViewer call');
        console.log(value,type,'updateViewer call')
        // console.dir(data);
      }
  }

  const customSkuInput = document.querySelector('#custom-sku');

  visualizerOptionsContainer.addEventListener('click', e => {
    const el = e.target;

    console.log('el', el);

    if (el.dataset.optionReference) {
      optionsContainer.querySelector(`input[value="${el.dataset.optionReference}"]`).nextElementSibling.click();
    }

    if (el.classList.contains('swatch--custom')) {

      const dataSku = el.dataset.sku;

      if (dataSku) {
        const customSkuInputValue = customSkuInput.value;
        const customSkuInputValueArray = customSkuInputValue.split('.');
        customSkuInputValueArray.splice((customSkuInputValueArray.length - 1 - parseInt(el.dataset.skuIndex)), 1, dataSku);
        const newCustomSkuInputValue = customSkuInputValueArray.join('.');
        customSkuInput.value = newCustomSkuInputValue;

        const groupLegend = el.closest('.custom-option');
        groupLegend.querySelector('.radio__legend__selected-option-label').innerHTML = ` - <strong>${el.value}</strong>`;
      }

      const wrapperContainer = el.closest('.selector-wrapper--visualizer');
      const propertyInput = wrapperContainer.querySelector('input[type="hidden"]');
      propertyInput.value = el.value;
      // console.log(el.dataset.value, el.dataset.group);
      // console.log(el.dataset.id, el.dataset.group);
      if(woodTopConfigIds.includes(imagineUUid)){
          // console.log("custommmm")
            // const visualizerData = getSelectedVisualizerData();
            setTimeout(() => {
              getSelectedVisualizerData(el.dataset.id, el.dataset.group)
            }, 100);

            // console.log(visualizerData,"visualizerData");

        }else if(shelfiePaintedConfigIds.includes(imagineUUid)){
          // console.log(el.dataset.group === 'Paint Color' ? 'Wood Top' : 'Paint Color');
          const updatedShelfieLabel = el.dataset.group === 'Paint Color' ? 'Wood Top' : 'Paint Color'
          // console.log('callexczxccczd',el.dataset.group);
          updateViewer(el.dataset.id, updatedShelfieLabel);
        }else{
          console.log(el.dataset.id, el.dataset.group);
          updateViewer(el.dataset.id, el.dataset.group);
        }
      // updateViewer(el.dataset.value, el.dataset.group);

    }

  }, false);

  // optionsContainer.addEventListener('click', e => {
  //   console.log("target")
  //   const el = e.target;

  //   if (el.classList.contains('radio__input')) {
  //     if (el.name !== "options[Grade]") {
  //       // console.log("el:",el)
  //       console.log("values:",el.dataset.value, "type:", el.dataset.group)
  //       if(woodTopConfigIds.includes(imagineUUid)){
  //         // console.log("custommmm")
  //           const visualizerData = getSelectedVisualizerData(el.dataset.value, el.dataset.group);

  //           console.log(visualizerData,"visualizerData");
  //       }else if(shelfiePaintedConfigIds.includes(imagineUUid)){
  //         // console.log(el.dataset.group === 'Paint Color' ? 'Wood Top' : 'Paint Color');
  //         const updatedShelfieLabel = el.dataset.group === 'Paint Color' ? 'Wood Top' : 'Paint Color'
  //         // console.log('callexczxccczd',el.dataset.group);
  //         updateViewer(el.dataset.id, updatedShelfieLabel);
  //       }else{
  //         if (!noCushionConfigIds.includes(imagineUUid) || el.dataset.group === 'Chair Frame') {
  //             updateViewer(el.dataset.value, el.dataset.group);
  //           }else{
  //             console.log(el.dataset.id, el.dataset.group);
  //           }
  //       }
  //     }
  //   }
  // }, false);

  visualizerOptionsContainer.querySelectorAll('.selector-wrapper--visualizer').forEach(el => {
    el.querySelector('.radio__legend__selected-option-label').innerHTML = ` - <strong>${el.querySelector('.radio__input:checked').value}</strong>`;

    if (el.querySelector('.radio__input:checked').dataset.optionReference) {
      optionsContainer.querySelector(`input[value="${el.querySelector('.radio__input:checked').dataset.optionReference}"]`).nextElementSibling.click();
    }

    const visualizerUUID = el.querySelector('.radio__input:checked').dataset.visualizerUuid;

    if (visualizerUUID) {
      visualizerAPI.configurator.setActiveByUuid(visualizerUUID);
    }
  });

  optionsContainer.querySelectorAll('.selector-wrapper').forEach(el => {
    const visualizerUUID = el.querySelector('.radio__input:checked').dataset.visualizerUuid;

    if (visualizerUUID) {
      visualizerAPI.configurator.setActiveByUuid(visualizerUUID);
    }
  });


  const customOptions = document.querySelectorAll('.radio__button--custom');

  customOptions.forEach(option => {
    option.addEventListener('click', e => {
      const target = e.currentTarget;
      const child = target.firstElementChild;
      const parent = target.closest('.custom-option');

      if (child.hasAttribute('data-group-name')) {
        parent.nextElementSibling.value = child.value;

        const optionLabel = parent.querySelector('.radio__legend__selected-option-label');

        if (!!(optionLabel)) {
          optionLabel.firstElementChild.innerText = child.value;
        }
      }
    });
  });

  const htmlElement = document.querySelector('html');

  if (!htmlElement.classList.contains('supports-touch')) {
    customOptions.forEach(option => {
      option.addEventListener('mouseenter', e => {

        const el = e.target;
        const parent = el.closest('.custom-option');
        const optionTooltip = parent.querySelector('.option-tooltip');
        const optionTooltipImage = optionTooltip.querySelector('img');
        const optionTooltipTitle = optionTooltip.querySelector('p');
        const optionTitle = el.querySelector('.radio__input').value;
        const optionTooltipImageSrc = el.querySelector('.swatch--custom').dataset.tooltipSrc;

        optionTooltipImage.src = optionTooltipImageSrc;
        optionTooltipImage.alt = `${optionTitle} fabric`;
        optionTooltipTitle.innerHTML = optionTitle;
        optionTooltip.style.display = 'block';
      }, false);

      option.addEventListener('mouseleave', e => {
        const el = e.target;
        const parent = el.closest('.custom-option');
        const optionTooltip = parent.querySelector('.option-tooltip');

        optionTooltip.style.display = 'none';
      }, false);
    });
  }

});
